import React, { useState, useEffect } from 'react';
import { faQuestion } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import InputLabel from '../InputLabel/InputLabel';
import { loadPopovers } from '../Popovers/Popovers';
import { formatNumber, getCurrencySymbol } from '../Formatter';
import { CommonValidation } from '../CommonValidation/commonValidation';

export interface PriceInputProps {
  /** The name reference to the component */
  name: string;
  /** An index of what piece of data is being modified */
  datakey?: string;
  /** An error message to display under the component */
  error?: string;
  /** The currency symbol that should be used defaults to £ */
  currency?: string;
  /** The label that appears above the component */
  label?: string;
  /** The placeholder text that appears in the textbox */
  placeholder?: string;
  /** The content of the help pop up if it is needed */
  helpContent?: string;
  /** The current value of the component */
  value?: string;
  /** Decides if the component shows a label */
  floatingLabel?: boolean;
  /** Decides if the component is editable */
  readonly?: boolean;
  /** Decides if the component is editable */
  disabled?: boolean;
  /** Decides if the component is has a help option */
  help?: boolean;
  /** Decides if the component is focused automatically on page load */
  autoFocus?: boolean;
  /** A function to be called when bluring out of the component */
  onLeave?: (event: React.FormEvent<HTMLInputElement>, key: string) => void;
  /** A function to specify what should happen when the component is focused */
  onFocus?: () => void;
  /** A function to be called on every key stroke to pass the value back up to it's controller */
  onChange?: (event: React.FormEvent<HTMLInputElement>, key: string) => void;
  culture: string;
}

export enum CurrencySymbols {
  pound = '\u00A3',
  euro = '\u20AC',
  dollar = '\u0024',
  koreanWon = '\u20A9',
  chineseYen = '\u00A5',
  brazilianLira = 'R\u0024'
}

/** A component that displays a currency symbol to the left and can have a help pop over as well */
const PriceInput = ({
  name,
  datakey = '',
  error,
  currency = getCurrencySymbol(),
  label,
  placeholder,
  helpContent,
  value,
  floatingLabel,
  readonly,
  disabled,
  help,
  autoFocus,
  onLeave,
  onFocus,
  onChange,
  culture
}: PriceInputProps): JSX.Element => {
  useEffect(() => {
    loadPopovers();
  }, []);

  const [editing, setEditing] = useState(false);

  const onFocusEvent = () => {
    if (onFocus) onFocus();
    setEditing(true);
  };

  const onLeaveEvent = (event: React.FormEvent<HTMLInputElement>) => {
    let value = event.currentTarget.value;

    if (!CommonValidation.isEmpty(value)) {
      value = updateValue(value);
      onChangeEvent(event);
      setEditing(false);
    }

    if (onLeave) {
      onLeave(event, datakey);
    }
  };

  const onChangeEvent = (event: React.FormEvent<HTMLInputElement>) => {
    onChange && onChange(event, datakey);
  };

  const updateValue = (textValue: string) => {
    let controlValue = textValue;

    const isValid =
      !CommonValidation.isEmpty(controlValue) &&
      CommonValidation.isValidDecimal(controlValue);
    if (isValid) {
      controlValue = controlValue.trim();
      const decimalFixedValue = parseFloat(controlValue).toFixed(2);
      const decimalValue = parseFloat(controlValue);

      if (parseInt(decimalFixedValue, 10) != decimalValue)
        controlValue = decimalFixedValue;
      else controlValue = decimalValue.toString();
    }

    return controlValue;
  };

  let styleWrapper = 'form-group';
  styleWrapper += error ? ' has-error' : '';

  let inputClasses = 'field small input-group';
  inputClasses += value && parseInt(value, 10) < 0 ? ' warning' : '';

  let inputInnerStyle = 'form-control';
  if (floatingLabel && label) {
    inputInnerStyle += error ? ' is-invalid' : '';
  }

  let valueCopy = value;

  if (!editing) {
    if (CommonValidation.isValidDecimal(valueCopy || '')) {
      const textInputValue = valueCopy ?? '0';
      valueCopy = formatNumber(parseFloat(textInputValue), 2, culture);
    }
  }

  const createFinalControl = () => {
    return (
      <div className={styleWrapper}>
        {label && <InputLabel labelText={label} name={name} />}
        <div className={inputClasses}>
          <span
            className='input-group-text'
            aria-describedby='button-addon-price'>
            {currency}
          </span>
          <input
            id={name}
            type='text'
            autoComplete='off'
            className={inputInnerStyle}
            autoFocus={autoFocus}
            placeholder={placeholder}
            name={name}
            value={valueCopy}
            onChange={onChangeEvent}
            onFocus={onFocusEvent}
            onBlur={onLeaveEvent}
            readOnly={readonly}
            disabled={disabled}
          />
          {help && (
            <a
              id='button-addon-price'
              tabIndex={0}
              type='button'
              style={{ padding: '0.5rem 0.5rem' }}
              className='btn btn-sm btn-primary'
              title={label}
              data-bs-toggle='popover'
              data-html='true'
              data-bs-trigger='focus'
              data-bs-content={helpContent}>
              <FontAwesomeIcon icon={faQuestion} />
            </a>
          )}
        </div>
        <div className='input'>{error}</div>
      </div>
    );
  };

  const finalControl = createFinalControl();

  return <>{finalControl}</>;
};

export default PriceInput;
